import { ReactNode, useMemo, useState } from 'react'
import AdvancedComboBox from '../../../components/AdvancedComboBox'
import FormRow from '../../../components/FormRow'
import useStorePartsSearch from '../../../hooks/storePartsSearch'
import { StorePartsQuery } from '../../../graphql'

export type SubstitutePartOption = StorePartsQuery['storeParts'][number]

type SubstitutePartPickerProps = {
	fulfillingInventoryId: string,
	selectedOption?: SubstitutePartOption,
	onChange: ( option?: SubstitutePartOption ) => void,
	label?: ReactNode,
	hideLabel?: boolean,
	isDisabled?: boolean,
	fullWidth?: boolean,
	debounceKey?: string,
}

export default function SubstitutePartPicker( {
	fulfillingInventoryId,
	selectedOption,
	onChange,
	label,
	hideLabel = false,
	isDisabled,
	fullWidth,
	debounceKey,
}: SubstitutePartPickerProps ): JSX.Element {
	const [ searchString, setSearchString ] = useState( '' )
	const storeId = Number.parseInt( fulfillingInventoryId, 10 )
	const { loading, data } = useStorePartsSearch(
		Number.isFinite( storeId ) ? storeId : undefined,
		searchString,
		debounceKey ?? `substitute-part-picker-${fulfillingInventoryId}`,
	)

	const candidates = useMemo( () => {
		return ( data?.storeParts ?? [] ).filter( ( sp ) => sp.part.hasActiveItemMaster )
	}, [ data ] )

	const comboBox = (
		<AdvancedComboBox
			debounceDelayMs={400}
			setSearchString={setSearchString}
			setSelectedOption={onChange}
			data={candidates}
			getName={( sp ) => `${sp.part.partNumber ?? '—'} - ${sp.part.partClass.description}`}
			getSecondaryText={( sp ) => [
				sp.part.provider.name,
				`Available: ${sp.availableQuantity}`,
			]}
			selectedOption={selectedOption}
			isLoading={loading}
			fullWidth={fullWidth}
			isDisabled={isDisabled}
		/>
	)

	if ( hideLabel ) {
		return comboBox
	}

	return (
		<FormRow label={label ?? 'Substitute part'} fullWidth={fullWidth}>
			{comboBox}
		</FormRow>
	)
}
